import { useEffect, useState } from "react";

// Icons use Material Symbols, a ligature font: until it loads, every icon
// renders as its name ("home", "settings") instead of a glyph. Inter is waited
// on too, so text doesn't reflow when it swaps in. A slow or blocked font
// download gives up after this long and shows the app anyway.
const FONTS_TIMEOUT_MS = 3000;

let fontsLoaded = false;
let fontsReady: Promise<void> | null = null;

/**
 * Starts downloading the app's fonts, if it hasn't already. Call it as early
 * as we know the app (not the marketing site) is about to render, so the
 * download overlaps sign-in rather than following it.
 */
export function startLoadingFonts(): Promise<void> {
  if (!fontsReady) {
    fontsReady = loadFonts().then(() => {
      fontsLoaded = true;
    });
  }
  return fontsReady;
}

function loadFonts(): Promise<void> {
  // document.fonts.load fetches a face as soon as it's asked for, rather than
  // waiting for something on the page to use it.
  if (typeof document === "undefined" || !document.fonts?.load) {
    return Promise.resolve();
  }
  const loads = Promise.all([
    document.fonts.load('24px "Material Symbols Rounded"', "home"),
    document.fonts.load('16px "InterVariable"'),
  ]).then(
    () => undefined,
    () => undefined,
  );
  const timeout = new Promise<void>((resolve) =>
    setTimeout(resolve, FONTS_TIMEOUT_MS),
  );
  return Promise.race([loads, timeout]);
}

/** Whether the fonts are ready, or have timed out. Starts them if needed. */
export function useFontsReady(): boolean {
  const [ready, setReady] = useState(fontsLoaded);

  useEffect(() => {
    if (ready) return;
    let mounted = true;
    startLoadingFonts().then(() => {
      if (mounted) setReady(true);
    });
    return () => {
      mounted = false;
    };
  }, [ready]);

  return ready;
}
